<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import type { QualityPreset } from '../types';
import { QUALITY_PRESETS } from '../composables/useWebRTC';

const props = defineProps<{
  isStreaming: boolean;
  currentStreamer?: { id: string; name: string; isStreaming: boolean; isSelf?: boolean } | null;
  channelId: string;
  userName: string;
  userAvatar: string;
  localStream: MediaStream | null;
  selectedQuality: QualityPreset;
  connectionState: string;
  lastError: string | null;
}>();

const emit = defineEmits<{
  (e: 'start-screen', audioMode: 'system' | 'none' | 'device', audioDeviceId?: string): void;
  (e: 'stop-stream'): void;
  (e: 'update:quality', quality: QualityPreset): void;
}>();

// ── Detecção e Pareamento com o Nexia Streamer Desktop ──────────────────────
const isDesktopOnline = ref(false);
const isDesktopStreaming = ref(false);
const isDesktopChecking = ref(false);
let desktopCheckInterval: number | undefined;

async function checkDesktopApp() {
  isDesktopChecking.value = true;
  try {
    const res = await fetch('http://127.0.0.1:48123/status', {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    if (res.ok) {
      const data = await res.json();
      isDesktopOnline.value = true;
      isDesktopStreaming.value = !!data.isStreaming;

      // Envia automaticamente a sala para o desktop se emparelhar sozinho
      if (props.channelId && (!data.currentRoom || data.currentRoom !== props.channelId)) {
        await fetch('http://127.0.0.1:48123/pair', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            roomId: props.channelId,
            streamerName: props.userName,
          }),
        }).catch(() => {});
      }
    } else {
      isDesktopOnline.value = false;
      isDesktopStreaming.value = false;
    }
  } catch (e) {
    isDesktopOnline.value = false;
    isDesktopStreaming.value = false;
  } finally {
    isDesktopChecking.value = false;
  }
}

async function focusDesktopApp() {
  try {
    await fetch('http://127.0.0.1:48123/focus', { method: 'POST' });
  } catch (e) {
    window.location.href = `nexia-live://room/${encodeURIComponent(props.channelId)}?name=${encodeURIComponent(props.userName)}`;
  }
}

async function stopDesktopStream() {
  try {
    await fetch('http://127.0.0.1:48123/stop', { method: 'POST' });
    isDesktopStreaming.value = false;
  } catch (e) {}
}

onMounted(() => {
  checkDesktopApp();
  desktopCheckInterval = window.setInterval(checkDesktopApp, 2500);
});

onUnmounted(() => {
  if (desktopCheckInterval) clearInterval(desktopCheckInterval);
});

// ── Status de Áudio durante transmissão ───────────────────────────────────
const isAudioMuted = ref(false);
const showAudioTips = ref(false);

const hasAudioTrack = computed(() => {
  return !!props.localStream && props.localStream.getAudioTracks().length > 0;
});

function toggleMuteStream() {
  if (!props.localStream) return;
  const audioTracks = props.localStream.getAudioTracks();
  if (audioTracks.length > 0) {
    isAudioMuted.value = !isAudioMuted.value;
    audioTracks.forEach((t) => {
      t.enabled = !isAudioMuted.value;
    });
  }
}

// ── Fontes de Janelas / Jogos do Desktop Companion ─────────────────────────
interface DesktopSource {
  id: string;
  name: string;
  thumbnail: string;
  appIcon?: string | null;
  isDiscord?: boolean;
}

const selectedSource = ref<DesktopSource>({
  id: 'screen:0:0',
  name: 'Tela Completa (Monitor 1)',
  thumbnail: '',
});

const isSomeoneElseStreaming = computed(() => {
  return !!props.currentStreamer?.isStreaming && !props.currentStreamer?.isSelf;
});

const isStarting = ref(false);

async function handleStartStream(preferCompanion = true) {
  if (isStarting.value) return;

  if (isSomeoneElseStreaming.value) {
    alert(`⚠️ Transmissão Bloqueada:\n\n${props.currentStreamer?.name} já está transmitindo tela nesta chamada!\nApenas 1 pessoa pode transmitir por vez.`);
    return;
  }

  isStarting.value = true;

  try {
    if (preferCompanion) {
      // 1. Aciona o protocolo nativo do Windows nexia-live:// (ignora bloqueios de Mixed Content / CSP do navegador)
      const protocolUrl = `nexia-live://start?room=${encodeURIComponent(props.channelId)}&name=${encodeURIComponent(props.userName)}`;
      console.log('[Nexia Studio] Acionando aplicativo Desktop Anti-Eco via protocolo:', protocolUrl);
      window.location.href = protocolUrl;

      // 2. Envia também requisição para a porta local forçando SEMPRE a tela inteira (screen:0:0)
      try {
        await fetch('http://127.0.0.1:48123/start', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sourceId: 'screen:0:0',
            sourceName: 'Tela Completa',
            roomId: props.channelId,
            streamerName: props.userName,
            quality: props.selectedQuality,
          }),
        });
      } catch (err) {
        console.warn('[Nexia Studio] Porta local não alcançada (comum em navegadores HTTPS):', err);
      }

      isDesktopStreaming.value = true;
      return;
    }

    // Fallback: Compartilhamento direto nativo do navegador (tela cheia sem aba do app)
    emit('start-screen', 'system');
  } finally {
    isStarting.value = false;
  }
}

async function handleStopAll() {
  if (isDesktopStreaming.value) {
    await stopDesktopStream();
  }
  emit('stop-stream');
}


const previewVideoRef = ref<HTMLVideoElement | null>(null);

watch(
  () => props.localStream,
  (stream: MediaStream | null) => {
    if (previewVideoRef.value) {
      previewVideoRef.value.srcObject = stream;
    }
  },
  { immediate: true }
);
</script>


<template>
  <div class="flex flex-col h-screen w-screen bg-[#0e1013] text-[#dbdee1] font-sans select-none overflow-y-auto">
    <!-- Header do Estúdio -->
    <header class="h-14 border-b border-white/10 px-6 flex items-center justify-between bg-[#141517] flex-shrink-0">
      <div class="flex items-center gap-3">
        <div class="w-8 h-8 rounded-lg bg-gradient-to-tr from-[#5865F2] to-purple-600 flex items-center justify-center text-white shadow-md">
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </div>
        <div>
          <h1 class="text-sm font-bold text-white flex items-center gap-2">
            Nexia Live <span class="text-[10px] text-purple-400 bg-purple-500/15 px-2 py-0.5 rounded font-mono font-medium border border-purple-500/30">ESTÚDIO DE TRANSMISSÃO</span>
          </h1>
          <p class="text-[11px] text-[#949ba4]">
            Canal de Voz: <strong class="text-white">{{ channelId }}</strong>
          </p>
        </div>
      </div>

      <!-- Status de Conexão com o Discord -->
      <div class="flex items-center gap-3">
        <div
          class="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border"
          :class="{
            'bg-[#23a55a]/10 border-[#23a55a]/30 text-[#23a55a]': connectionState === 'connected',
            'bg-[#f0b232]/10 border-[#f0b232]/30 text-[#f0b232]': connectionState === 'connecting',
            'bg-[#f23f43]/10 border-[#f23f43]/30 text-[#f23f43]': connectionState === 'disconnected' || connectionState === 'error',
          }"
        >
          <span
            class="w-2 h-2 rounded-full"
            :class="{
              'bg-[#23a55a] animate-pulse': connectionState === 'connected',
              'bg-[#f0b232]': connectionState === 'connecting',
              'bg-[#f23f43]': connectionState === 'disconnected' || connectionState === 'error',
            }"
          ></span>
          <span>{{ connectionState === 'connected' ? 'Conectado à Chamada' : 'Conectando...' }}</span>
        </div>

        <!-- Status do Nexia Streamer Desktop -->
        <div
          v-if="isDesktopOnline"
          class="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border shadow-xs cursor-pointer transition"
          :class="isDesktopStreaming ? 'bg-purple-500/20 border-purple-500/50 text-purple-300' : 'bg-[#23a55a]/15 border-[#23a55a]/40 text-[#23a55a]'"
          @click="focusDesktopApp"
          :title="isDesktopStreaming ? 'App Desktop está transmitindo ao vivo!' : 'O aplicativo desktop está conectado e pronto para transmitir sem áudio do Discord!'"
        >
          <span
            class="w-2 h-2 rounded-full"
            :class="isDesktopStreaming ? 'bg-purple-400 animate-ping' : 'bg-[#23a55a] animate-pulse'"
          ></span>
          <span>{{ isDesktopStreaming ? 'Desktop: Transmitindo (Sem Eco)' : 'Nexia Desktop Ativo (Sem Eco)' }}</span>
        </div>

        <!-- Botão Baixar Anti-Eco no Topo se estiver offline -->
        <a
          v-else
          href="https://nexia-live-signaling.nexia-app.workers.dev/download/NexiaStreamerSetup.exe?v=2.0.0"
          download="NexiaStreamerSetup.exe"
          target="_blank"
          class="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[#5865F2] hover:bg-[#4752C4] text-white shadow-xs transition active:scale-95 no-underline cursor-pointer"
          title="Baixar instalador oficial v2.0.0 para transmitir jogos sem o áudio do Discord"
        >
          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          <span>Baixar App v2.0.0 (.exe)</span>
        </a>

        <div class="flex items-center gap-2 border-l border-white/10 pl-3">
          <img :src="userAvatar" class="w-7 h-7 rounded-full bg-[#2b2d31] object-cover" />
          <span class="text-xs font-semibold text-white">{{ userName }}</span>
        </div>
      </div>
    </header>

    <!-- Erro se houver -->
    <div v-if="lastError" class="m-4 p-3 rounded-lg bg-[#f23f43]/15 border border-[#f23f43]/40 text-[#f23f43] text-xs flex items-center justify-between">
      <span>⚠️ {{ lastError }}</span>
    </div>

    <!-- Conteúdo Central do Estúdio -->
    <main class="flex-1 flex items-center justify-center p-6">
      <!-- CASO 1: NÃO ESTÁ TRANSMITINDO (Painel de Configuração para Iniciar) -->
      <div v-if="!isStreaming" class="max-w-md w-full bg-[#1e1f22] border border-white/10 rounded-2xl p-6 shadow-2xl space-y-6">
        <div class="text-center space-y-2">
          <div class="w-16 h-16 mx-auto rounded-2xl bg-[#5865F2]/10 border border-[#5865F2]/30 flex items-center justify-center text-[#5865F2]">
            <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 class="text-lg font-bold text-white">Transmita sua Tela para o Discord</h2>
          <p class="text-xs text-[#949ba4] leading-relaxed">
            Seus amigos vão assistir ao vivo em tempo real diretamente dentro da atividade aberta no Discord.
          </p>
        </div>

        <!-- Banner Pro: Nexia Streamer Desktop com Exclusão Automática do Discord -->
        <!-- SUB-CASO 1: Desktop Online e Transmitindo -->
        <div
          v-if="isDesktopOnline && isDesktopStreaming"
          class="p-4 rounded-xl bg-purple-500/15 border border-purple-500/40 flex flex-col gap-3 animate-fade-in"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span class="w-2.5 h-2.5 rounded-full bg-purple-400 animate-ping"></span>
              <strong class="text-xs text-white">Transmissão Ativa via Nexia Desktop</strong>
            </div>
            <span class="text-[9px] text-purple-300 bg-purple-500/25 px-2 py-0.5 rounded font-bold uppercase">Anti-Eco Ativo</span>
          </div>
          <p class="text-[11px] text-[#dbdee1]/80 leading-relaxed text-left">
            O aplicativo desktop está transmitindo o som exclusivo do seu jogo diretamente para a chamada, sem capturar a voz dos amigos.
          </p>
          <div class="flex items-center gap-2">
            <button
              @click="focusDesktopApp"
              class="flex-1 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold cursor-pointer transition active:scale-95"
            >
              Abrir Janela do App
            </button>
            <button
              @click="stopDesktopStream"
              class="py-1.5 px-3 rounded-lg bg-[#f23f43] hover:bg-[#d83a3e] text-white text-xs font-semibold cursor-pointer transition active:scale-95"
            >
              Parar Transmissão
            </button>
          </div>
        </div>

        <!-- SUB-CASO 2: Desktop Online e Conectado (Pronto para Transmitir) -->
        <div
          v-else-if="isDesktopOnline"
          class="p-4 rounded-xl bg-[#23a55a]/10 border border-[#23a55a]/30 flex flex-col gap-3 animate-fade-in text-left"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span class="text-lg">⚡</span>
              <div>
                <div class="text-xs font-bold text-white flex items-center gap-1.5">
                  <span>Nexia Desktop Conectado</span>
                  <span class="text-[9px] text-[#23a55a] bg-[#23a55a]/20 px-1.5 py-0.5 rounded font-bold">ANTI-ECO ATIVO</span>
                </div>
                <p class="text-[10px] text-[#949ba4] mt-0.5">
                  O aplicativo local conectou sozinho com este canal.
                </p>
              </div>
            </div>
          </div>

          <!-- Modo Tela Inteira Direto (Sem travar em janelas) -->
          <div class="p-3.5 rounded-xl bg-black/40 border border-[#23a55a]/30 flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-[#23a55a]/15 border border-[#23a55a]/30 flex items-center justify-center text-xl flex-shrink-0">
              🖥️
            </div>
            <div>
              <div class="text-xs font-bold text-white flex items-center gap-1.5">
                <span>Modo: Tela Completa (Monitor 1)</span>
                <span class="text-[9px] text-[#23a55a] bg-[#23a55a]/20 px-1.5 py-0.5 rounded font-bold">AUTOMÁTICO</span>
              </div>
              <p class="text-[10.5px] text-[#949ba4] mt-0.5 leading-relaxed">
                Transmite a sua tela inteira sem travar em nenhuma janela específica. Tudo o que você abrir ou jogar é transmitido ao vivo.
              </p>
            </div>
          </div>

          <!-- Dica Prática de Isolamento de Voz do Discord -->
          <div class="p-3 rounded-xl bg-purple-500/10 border border-purple-500/30 text-left space-y-1">
            <div class="text-[11px] font-bold text-purple-300 flex items-center gap-1.5">
              <span>🎧</span>
              <span>Como isolar o áudio do Discord:</span>
            </div>
            <p class="text-[10.5px] text-[#dbdee1]/80 leading-relaxed">
              No seu Discord ➔ <strong>Configurações (engrenagem) ➔ Voz e Vídeo ➔ Dispositivo de Saída</strong>: selecione o seu <strong>Fone de Ouvido / Headset</strong> em vez de "Padrão". Assim, o som do seu jogo é transmitido e a voz dos seus amigos fica 100% isolada no seu fone!
            </p>
          </div>
        </div>

        <!-- SUB-CASO 3: Desktop Offline (Mostrar Botões de Download do Instalador) -->
        <div
          v-else
          class="p-4 rounded-xl bg-gradient-to-r from-[#5865F2]/20 via-purple-600/15 to-[#5865F2]/20 border border-[#5865F2]/40 flex flex-col gap-3 animate-fade-in text-left shadow-lg"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span class="text-base">🚀</span>
              <h3 class="text-xs font-bold text-white">Transmita Jogos sem Eco do Discord</h3>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="text-[9px] font-bold text-[#23a55a] bg-[#23a55a]/20 px-2 py-0.5 rounded uppercase">v2.0.0 (Motor OBS 60fps & Anti-Eco)</span>
              <span class="text-[9px] font-bold text-[#5865F2] bg-[#5865F2]/20 px-2 py-0.5 rounded uppercase">Recomendado</span>
            </div>
          </div>

          <p class="text-[11px] text-[#949ba4] leading-relaxed">
            Instale o <strong>Nexia Streamer v2.0.0</strong> com motor OBS embutido. Ele roda em segundo plano, detecta sua placa de vídeo automaticamente e transmite em 60fps lisos sem vazar o áudio do Discord! <strong>Instale uma única vez e pronto!</strong>
          </p>

          <div class="flex flex-col gap-2 pt-1">
            <!-- Botão Principal de Download (.exe) -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <a
                href="https://nexia-live-signaling.nexia-app.workers.dev/download/NexiaStreamerSetup.exe?v=2.0.0"
                download="NexiaStreamerSetup.exe"
                target="_blank"
                class="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-gradient-to-r from-[#5865F2] to-purple-600 hover:from-[#4752C4] hover:to-purple-700 text-white text-xs font-bold transition cursor-pointer shadow-lg shadow-[#5865F2]/30 no-underline active:scale-98"
              >
                <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Instalador (.exe)</span>
              </a>

              <!-- Botão Alternativo (.zip) que evita bloqueios automáticos do navegador -->
              <a
                href="https://nexia-live-signaling.nexia-app.workers.dev/download/NexiaStreamerSetup.zip?v=2.0.0"
                download="NexiaStreamerSetup.zip"
                target="_blank"
                class="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-[#dbdee1] hover:text-white text-xs font-semibold transition cursor-pointer no-underline active:scale-98"
                title="Versão compactada em ZIP para navegadores que bloqueiam .exe direto"
              >
                <svg class="w-4 h-4 shrink-0 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                <span>Compactado (.zip)</span>
              </a>
            </div>

            <!-- Dica sobre o SmartScreen do Windows -->
            <div class="px-2.5 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-[10.5px] text-amber-300/90 leading-tight">
              ⚠️ <strong>Aviso do Windows:</strong> Se o SmartScreen avisar "Editor Desconhecido", clique em <u>Mais informações</u> ➔ <u>Executar assim mesmo</u>.
            </div>

            <div class="flex items-center justify-between text-[11px] text-[#949ba4] pt-0.5 px-0.5">
              <span>Já instalou no seu PC?</span>
              <button
                @click="handleStartStream(true)"
                class="hover:text-white transition cursor-pointer font-bold text-[#23a55a] flex items-center gap-1"
                title="Abre e sincroniza com o aplicativo instalado no seu Windows"
              >
                <span>🚀 Conectar Anti-Eco Agora</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Seletor de Qualidade -->
        <div class="space-y-2">
          <label class="text-xs font-semibold text-[#dbdee1] flex items-center justify-between">
            <span>Qualidade de Transmissão</span>
            <span class="text-[10px] text-[#949ba4]">Tempo Real</span>
          </label>
          <div class="grid grid-cols-2 gap-2">
            <button
              v-for="q in QUALITY_PRESETS"
              :key="q.id"
              @click="emit('update:quality', q)"
              class="p-2.5 rounded-lg border text-left transition cursor-pointer text-xs"
              :class="selectedQuality.id === q.id ? 'bg-[#5865F2]/20 border-[#5865F2] text-white font-semibold shadow-xs' : 'bg-[#141517] border-white/5 hover:border-white/15 text-[#949ba4]'"
            >
              <div class="font-bold">{{ q.id === 'source' ? 'Original (Fonte)' : (q.id === '1440p60' ? '1440p 2K' : (q.id === '1080p60ultra' ? '1080p Ultra' : (q.id === '1080p60' ? '1080p Pro' : '720p Fluído'))) }}</div>
              <div class="text-[10.5px] text-[#23a55a] font-mono mt-0.5">{{ q.fps }} FPS • {{ (q.maxBitrateKbps / 1000).toFixed(1) }} Mbps</div>
            </button>
          </div>
        </div>

        <!-- Modo de Áudio Simples -->
        <div class="p-3 rounded-xl bg-[#141517] border border-white/5 flex items-center justify-between text-xs">
          <div class="flex items-center gap-2.5">
            <span class="text-lg">{{ isDesktopOnline ? '🎮' : '🔊' }}</span>
            <div>
              <div class="font-semibold text-white">
                {{ isDesktopOnline ? 'Anti-Eco Ativo (Áudio do Jogo)' : 'Áudio da Tela / Jogo' }}
              </div>
              <div class="text-[10px] text-[#949ba4]">
                {{ isDesktopOnline ? 'O som do Discord não passa para a transmissão.' : 'Transmite o som da janela ou jogo selecionado.' }}
              </div>
            </div>
          </div>
          <span
            class="text-[9px] px-2 py-0.5 rounded font-bold uppercase"
            :class="isDesktopOnline ? 'bg-[#23a55a]/20 text-[#23a55a]' : 'bg-white/10 text-[#949ba4]'"
          >
            {{ isDesktopOnline ? 'Anti-Eco' : 'Padrão' }}
          </span>
        </div>

        <!-- Alerta de Bloqueio se outra pessoa estiver transmitindo -->
        <div
          v-if="isSomeoneElseStreaming"
          class="p-3.5 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center gap-3 text-left animate-fade-in"
        >
          <span class="text-2xl shrink-0">⚠️</span>
          <div>
            <div class="text-xs font-bold text-amber-300">
              Transmissão em andamento por {{ currentStreamer?.name }}
            </div>
            <div class="text-[11px] text-amber-200/80 mt-0.5 leading-relaxed">
              Apenas 1 pessoa pode transmitir tela por vez nesta chamada. Aguarde a transmissão atual ser encerrada para poder transmitir.
            </div>
          </div>
        </div>

        <!-- Botões de Iniciar Transmissão -->
        <div class="space-y-2 pt-1">
          <!-- Botão Principal: Transmitir com Anti-Eco (Aplicativo Desktop) -->
          <button
            @click="handleStartStream(true)"
            :disabled="isStarting || isSomeoneElseStreaming"
            class="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-[#23a55a] to-[#1f9350] hover:from-[#1f9350] hover:to-[#1a7f45] disabled:opacity-50 text-white font-bold text-sm shadow-lg shadow-[#23a55a]/25 transition active:scale-98 cursor-pointer flex items-center justify-center gap-2"
          >
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span>🚀 Iniciar Transmissão da Tela Completa (Anti-Eco)</span>
          </button>

          <!-- Botão Secundário: Transmissão direta pelo navegador -->
          <button
            @click="handleStartStream(false)"
            :disabled="isStarting || isSomeoneElseStreaming"
            class="w-full py-2.5 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-[#949ba4] hover:text-white font-medium text-xs transition cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-40"
            title="Compartilha a tela diretamente pelo navegador (sem isolamento de áudio do Discord)"
          >
            <span>🌐 Transmitir pelo Navegador (Sem Anti-Eco)</span>
          </button>
        </div>
      </div>


      <!-- CASO 2: ESTÁ TRANSMITINDO (Monitor de Preview + Controle de Parada + Mute de Áudio) -->
      <div v-else class="max-w-3xl w-full bg-[#1e1f22] border border-white/10 rounded-2xl p-6 shadow-2xl space-y-5">
        <div class="flex items-center justify-between pb-3 border-b border-white/10">
          <div class="flex items-center gap-3">
            <span class="flex h-3 w-3 relative">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#f23f43] opacity-75"></span>
              <span class="relative inline-flex rounded-full h-3 w-3 bg-[#f23f43]"></span>
            </span>
            <div>
              <h2 class="text-sm font-bold text-white flex items-center gap-2">
                TRANSMITINDO AO VIVO
              </h2>
              <p class="text-[11px] text-[#23a55a]">
                ● Seus amigos estão assistindo diretamente pela atividade no Discord
              </p>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <!-- Botão de Mutar / Desmutar Som do Stream -->
            <button
              v-if="hasAudioTrack"
              @click="toggleMuteStream"
              class="px-3 py-2 rounded-lg text-xs font-semibold transition cursor-pointer border flex items-center gap-1.5"
              :class="isAudioMuted ? 'bg-[#f23f43]/20 border-[#f23f43]/40 text-[#f23f43] hover:bg-[#f23f43]/30' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'"
              :title="isAudioMuted ? 'Clique para reativar o áudio do stream' : 'Clique para silenciar o áudio do stream'"
            >
              <svg v-if="!isAudioMuted" class="w-4 h-4 text-[#23a55a]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
              <svg v-else class="w-4 h-4 text-[#f23f43]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
              <span>{{ isAudioMuted ? 'Áudio Mutado' : 'Áudio do PC Ativo' }}</span>
            </button>

            <!-- Botão Encerrar -->
            <button
              @click="handleStopAll"
              class="px-4 py-2 rounded-lg bg-[#f23f43] hover:bg-[#d83a3e] text-white font-semibold text-xs transition cursor-pointer shadow-md shadow-[#f23f43]/30 active:scale-95 flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>Encerrar Transmissão</span>
            </button>
          </div>
        </div>

        <!-- Preview da Tela Transmitida -->
        <div class="relative w-full aspect-video bg-black rounded-xl overflow-hidden border border-white/10 flex items-center justify-center">
          <video
            ref="previewVideoRef"
            autoplay
            playsinline
            muted
            class="w-full h-full object-contain"
          ></video>
          <div class="absolute bottom-3 right-3 px-2 py-1 rounded bg-black/75 backdrop-blur-xs text-[10px] text-white/70 flex items-center gap-2">
            <span>Monitor de Preview</span>
            <span v-if="hasAudioTrack" class="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded" :class="isAudioMuted ? 'bg-[#f23f43]/30 text-[#f23f43]' : 'bg-[#23a55a]/30 text-[#23a55a]'">
              {{ isAudioMuted ? 'Áudio Mutado' : 'Com Áudio' }}
            </span>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

